import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const steps = [
  { id: 'basic', title: 'Basic Info' },
  { id: 'survey', title: 'Survey Template' },
  { id: 'settings', title: 'Campaign Settings' },
];

export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goal_type: 'representatives',
    target_representatives: 100,
    start_date: '',
    deadline: '',
    survey_template_id: undefined as string | undefined,
    access_mode: 'authenticated',
    require_gps: true,
    rate_limit_mode: 'per_place',
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => {
      const orgId = user?.organization_id;
      if (!orgId) {
        throw new Error('Organization not found. Please log out and log back in.');
      }
      return api.createCampaign({
        organization_id: orgId,
        name: data.name,
        description: data.description || undefined,
        goal_type: data.goal_type,
        target_representatives: data.target_representatives,
        start_date: data.start_date || undefined,
        deadline: data.deadline || undefined,
        survey_template_id: data.survey_template_id || undefined,
        access_mode: data.access_mode as 'authenticated' | 'pin' | 'anonymous',
        require_gps: data.require_gps,
        rate_limit_mode: data.rate_limit_mode as 'per_place' | 'per_device' | 'per_campaign',
        status: 'draft',
        geography_config: {},
        metadata: {},
      });
    },
    onSuccess: () => {
      setShowSuccess(true);
      setOrgError(null);
    },
    onError: (error: Error) => {
      setOrgError(error.message);
    },
  });

  const handleNext = () => {
    if (!user?.organization_id) {
      setOrgError('Organization not found. Please log out and log back in.');
      return;
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateField = (field: string, value: string | number | boolean | undefined) => {
    if (value !== undefined) {
      setFormData({ ...formData, [field]: value });
    } else {
      setFormData({ ...formData, [field]: undefined });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name *</Label>
              <Input
                id="name"
                placeholder="Enter campaign name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe your campaign"
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal_type">Goal Type</Label>
              <Select
                value={formData.goal_type}
                onValueChange={(value) => updateField('goal_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="representatives">Representatives</SelectItem>
                  <SelectItem value="locations">Locations</SelectItem>
                  <SelectItem value="responses">Responses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target">Target Number</Label>
              <Input
                id="target"
                type="number"
                min={1}
                value={formData.target_representatives}
                onChange={(e) => updateField('target_representatives', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => updateField('start_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deadline">Deadline</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => updateField('deadline', e.target.value)}
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Survey Template</Label>
              <Select
                value={formData.survey_template_id}
                onValueChange={(value) => updateField('survey_template_id', value === 'none' ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (create later)</SelectItem>
                  <SelectItem value="template-1">Customer Satisfaction Survey</SelectItem>
                  <SelectItem value="template-2">Product Feedback</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                You can also create a survey template after creating the campaign.
              </p>
            </div>
            <div className="text-center py-8">
              <Button variant="outline" onClick={() => navigate('/surveys/new?returnTo=/campaigns/new')}>
                Create New Survey Template
              </Button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="access_mode">Access Mode</Label>
              <Select
                value={formData.access_mode}
                onValueChange={(value) => updateField('access_mode', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="authenticated">Authenticated (Login required)</SelectItem>
                  <SelectItem value="pin">PIN Code</SelectItem>
                  <SelectItem value="anonymous">Anonymous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_mode">Rate Limit Mode</Label>
              <Select
                value={formData.rate_limit_mode}
                onValueChange={(value) => updateField('rate_limit_mode', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_place">Per Place (1 response per location)</SelectItem>
                  <SelectItem value="per_device">Per Device (1 response per device)</SelectItem>
                  <SelectItem value="per_campaign">Per Campaign (1 response per campaign)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="require_gps"
                checked={formData.require_gps}
                onChange={(e) => updateField('require_gps', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="require_gps">Require GPS verification</Label>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Create Campaign</h1>
        <p className="text-muted-foreground">Set up a new survey campaign</p>
      </div>

      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                index <= currentStep ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
            </div>
            <span className={`ml-2 ${index <= currentStep ? '' : 'text-muted-foreground'}`}>
              {step.title}
            </span>
            {index < steps.length - 1 && (
              <div className={`w-24 h-0.5 mx-4 ${index < currentStep ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>
            {currentStep === 0 && 'Enter basic information about your campaign'}
            {currentStep === 1 && 'Choose or create a survey template'}
            {currentStep === 2 && 'Configure campaign access and behavior settings'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orgError && (
            <div className="mb-4 p-3 text-sm bg-destructive/10 text-destructive rounded-md">
              {orgError}
            </div>
          )}
          {renderStep()}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0 || createMutation.isPending}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Campaign Created!</DialogTitle>
              <DialogDescription>
                Your campaign has been created successfully. You can now start adding target locations and launching it.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end space-x-2 mt-4">
              <Button variant="outline" onClick={() => navigate('/campaigns')}>
                View All Campaigns
              </Button>
              <Button onClick={() => navigate('/campaigns/new')}>
                Create Another
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Button onClick={handleNext} disabled={createMutation.isPending}>
          {currentStep === steps.length - 1 ? (
            createMutation.isPending ? (
              <>Creating...</>
            ) : (
              <>Create Campaign</>
            )
          ) : (
            <>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
